import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Monitor, Clock, ExternalLink, X, Plus } from 'lucide-react';
import { useApi } from '../auth/useApi';

/* ============================================================================
 * UserAccessPopover — click a name on a ticket → role + software + recent tickets.
 * Admins and techs (card.canManage) can also add/remove this user's software inline
 * (pick from the catalog, type a custom name, or paste comma-separated names).
 * Read-only for everyone else. No separate page.
 * ========================================================================== */

const C = {
  primary: 'var(--c-primary)', secondary: 'var(--c-secondary)', bg: 'var(--c-bg)', surface: 'var(--c-surface)',
  border: 'var(--c-border)', text: 'var(--c-text)', textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)', warn: 'var(--c-warn)', danger: 'var(--c-danger)',
};

const PRIORITY_COLOR: Record<string, string> = {
  Critical: '#c4533a', High: '#c4793a', Medium: '#7a9b7e', Low: '#9a9a9a',
};

interface AccessCard {
  user: { UserId: number; DisplayName: string; Email: string; Role: string; Active: boolean };
  software: { SoftwareId: number; Name: string; Notes: string | null }[];
  recentTickets: {
    TicketId: number; TicketKey: string; Subject: string;
    Priority: string; StatusName: string; CreatedAt: string; Relationship: string;
  }[];
  canManage: boolean;
}

interface CatalogRow { SoftwareCatalogId: number; Name: string; Category: string | null }

interface Props {
  userId: number;
  name: string;
  /** Optional route to a profile page. Omit until a /users/:id page exists. */
  profileTo?: string;
}

export function UserAccessPopover({ userId, name, profileTo }: Props) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [card, setCard] = useState<AccessCard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);

  // software management (only used when card.canManage)
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [picked, setPicked] = useState('');
  const [customName, setCustomName] = useState('');
  const [busy, setBusy] = useState(false);
  const [mgmtError, setMgmtError] = useState<string | null>(null);

  const loadCard = () => {
    setLoading(true);
    setError(null);
    return api.get<AccessCard>(`/api/users/${userId}/access-card`)
      .then(setCard)
      .catch((e: any) => setError(e?.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  };

  // fetch the card the first time the popover opens
  useEffect(() => {
    if (!open || card || loading) return;
    loadCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // load the catalog once, only if the viewer can manage
  useEffect(() => {
    if (!open || !card?.canManage || catalog.length) return;
    api.get<CatalogRow[]>('/api/users/software-catalog')
      .then(rows => setCatalog(Array.isArray(rows) ? rows : []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card?.canManage]);

  // close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const catalogSorted = useMemo(
    () => [...catalog].sort((a, b) =>
      (a.Category ?? '').localeCompare(b.Category ?? '') || a.Name.localeCompare(b.Name)),
    [catalog],
  );

  const addSoftware = async () => {
    const value = (picked === '__custom__' ? customName : picked).trim();
    if (!value) return;
    setBusy(true);
    setMgmtError(null);
    try {
      if (value.includes(',')) {
        await api.post(`/api/users/${userId}/software/bulk`, { names: value }); // dupes skipped server-side
      } else {
        await api.post(`/api/users/${userId}/software`, { name: value });
      }
      setPicked('');
      setCustomName('');
      await loadCard();
    } catch (e: any) {
      setMgmtError(e?.message ?? 'Add failed');
    } finally {
      setBusy(false);
    }
  };

  const removeSoftware = async (swId: number) => {
    setBusy(true);
    setMgmtError(null);
    try {
      await api.del(`/api/users/${userId}/software/${swId}`);
      await loadCard();
    } catch (e: any) {
      setMgmtError(e?.message ?? 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  const addDisabled = busy || !(picked === '__custom__' ? customName.trim() : picked);

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          color: C.primary, font: 'inherit', textDecorationLine: 'underline',
          textDecorationStyle: 'dotted',
        }}
      >
        {name}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`Access summary for ${name}`}
          style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 1000, marginTop: 6,
            width: 320, maxWidth: '90vw', background: C.surface,
            border: `1px solid ${C.border}`, borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 14,
            color: C.text, fontSize: 13, textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{card?.user.DisplayName ?? name}</div>
              {card && <div style={{ color: C.textMuted, fontSize: 12, wordBreak: 'break-all' }}>{card.user.Email}</div>}
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, flexShrink: 0 }}>
              <X size={16} />
            </button>
          </div>

          {card && (
            <span style={{
              display: 'inline-block', fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
              color: C.surface, background: C.primary, borderRadius: 999,
              padding: '2px 10px', marginBottom: 10,
            }}>
              {card.user.Role}{card.user.Active === false ? ' · inactive' : ''}
            </span>
          )}

          {loading && <div style={{ color: C.textMuted, padding: '6px 0' }}>Loading…</div>}
          {error && <div style={{ color: C.danger, padding: '6px 0' }}>{error}</div>}

          {card && (
            <>
              <Section icon={<Monitor size={13} />} label={`Software (${card.software.length})`}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 120, overflowY: 'auto' }}>
                  {card.software.length === 0 && <Muted>None recorded</Muted>}
                  {card.software.map(s => (
                    <span key={s.SoftwareId} title={s.Notes ?? undefined} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      fontSize: 11, background: C.bg, border: `1px solid ${C.border}`,
                      borderRadius: 4, padding: '2px 6px',
                    }}>
                      {s.Name}
                      {card.canManage && (
                        <button type="button" onClick={() => removeSoftware(s.SoftwareId)} disabled={busy}
                          aria-label={`Remove ${s.Name}`}
                          style={{
                            background: 'none', border: 'none', padding: 0, lineHeight: 0,
                            cursor: busy ? 'not-allowed' : 'pointer', color: C.textMuted,
                          }}>
                          <X size={11} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>

                {card.canManage && (
                  <>
                    <div style={{ display: 'flex', gap: 4, marginTop: 6, alignItems: 'center' }}>
                      <select value={picked} onChange={e => setPicked(e.target.value)} disabled={busy}
                        style={{
                          flex: 1, minWidth: 0, fontSize: 12, padding: '3px 4px',
                          border: `1px solid ${C.border}`, borderRadius: 4, background: C.surface,
                        }}>
                        <option value="">Add software…</option>
                        {catalogSorted.map(c => (
                          <option key={c.SoftwareCatalogId} value={c.Name}>
                            {c.Category ? `${c.Category} — ${c.Name}` : c.Name}
                          </option>
                        ))}
                        <option value="__custom__">+ Custom / comma-separated…</option>
                      </select>
                      <button type="button" onClick={addSoftware} disabled={addDisabled}
                        aria-label="Add software"
                        style={{
                          background: addDisabled ? C.border : C.primary, color: '#fff', border: 'none',
                          borderRadius: 4, padding: '4px 6px', lineHeight: 0,
                          cursor: addDisabled ? 'not-allowed' : 'pointer',
                        }}>
                        <Plus size={13} />
                      </button>
                    </div>
                    {picked === '__custom__' && (
                      <input value={customName} onChange={e => setCustomName(e.target.value)} disabled={busy}
                        placeholder="e.g. Excel, Word, Slack"
                        style={{
                          width: '100%', boxSizing: 'border-box', marginTop: 4, fontSize: 12,
                          padding: '4px 6px', border: `1px solid ${C.border}`, borderRadius: 4,
                        }} />
                    )}
                    {mgmtError && <div style={{ color: C.danger, fontSize: 11, marginTop: 4 }}>{mgmtError}</div>}
                  </>
                )}
              </Section>

              <Section icon={<Clock size={13} />} label={`Recent tickets (${card.recentTickets.length})`}>
                {card.recentTickets.length === 0
                  ? <Muted>None</Muted>
                  : card.recentTickets.map(t => (
                    <Link key={t.TicketId} to={`/tickets/${t.TicketId}`} onClick={() => setOpen(false)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0',
                        color: C.text, textDecoration: 'none',
                      }}>
                      <span title={t.Priority} style={{
                        width: 8, height: 8, borderRadius: 999, flexShrink: 0,
                        background: PRIORITY_COLOR[t.Priority] ?? C.textMuted,
                      }} />
                      <span style={{ fontFamily: 'monospace', color: C.primary, fontWeight: 600, flexShrink: 0 }}>{t.TicketKey}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.Subject}</span>
                      <span style={{ marginLeft: 'auto', color: C.textMuted, fontSize: 11, flexShrink: 0 }}>{t.StatusName}</span>
                    </Link>
                  ))}
              </Section>

              {profileTo && (
                <Link to={profileTo} onClick={() => setOpen(false)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 8,
                    color: C.primary, fontSize: 12, textDecoration: 'none', fontWeight: 600,
                  }}>
                  View profile <ExternalLink size={12} />
                </Link>
              )}
            </>
          )}
        </div>
      )}
    </span>
  );
}

function Section({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5, color: C.textMuted,
        fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4,
      }}>
        {icon}{label}
      </div>
      {children}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span style={{ color: C.textMuted, fontStyle: 'italic' }}>{children}</span>;
}
